import { apiErrorMessage } from '@/api/client';
import { itemsApi } from '@/api/items.api';
import type { RegexTestResult } from '@/types/categories.types';
import type { CSSProperties, FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** The prototype's sample text for the tester. */
export const SAMPLE_TEXT =
  'Smlouva o dílo č. 2026/0412\n\nObjednatel: Vodárny Vysočina, s.r.o., IČO 27455131\nZhotovitel: Ing. Marek Doležal, nar. 12. 3. 1981, rodné číslo 810312/1004\nKontakt: marek.dolezal@example.cz, tel. +420 604 218 733\n\nCena díla činí 1 250 000 Kč bez DPH, splatnost 30 dnů od převzetí.\nPřístup do testovacího prostředí: https://scada-test.bvk.cz/login, heslo: Vodarna2026';

/** How long typing must pause before the backend is asked again. */
const DEBOUNCE_MS = 300;

const MATCH_STYLE: CSSProperties = {
  background: '#ffe08a',
  color: '#1b2733',
  boxShadow: '0 0 0 1px #e0b400',
  fontWeight: 600,
};

type Outcome = { kind: 'idle' } | { kind: 'result'; result: RegexTestResult } | { kind: 'failed'; message: string };

const SECTION = 'text-[10.5px] font-semibold uppercase tracking-[.1em] text-label-2';

/** The status pill colours of the prototype: neutral, invalid, found, none. */
const statusStyle = (bg: string, fg: string): CSSProperties => ({ background: bg, color: fg });

interface RegexTesterProps {
  pattern: string;
  sample: string;
  onSampleChange: (sample: string) => void;
}

/**
 * The regex tester under a regex item: a sample text and the matches highlighted in it.
 *
 * Matching runs on the backend (`POST /items/regex-test`, Python `re`, case-insensitive), so what
 * the tester shows is what detection will do — a JavaScript RegExp would disagree on syntax.
 */
export const RegexTester: FC<RegexTesterProps> = ({ pattern, sample, onSampleChange }) => {
  const { t } = useTranslation();
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'idle' });

  useEffect(() => {
    if (!pattern.trim()) {
      setOutcome({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      itemsApi
        .regexTest(pattern.trim(), sample)
        .then((result) => {
          if (!cancelled) setOutcome({ kind: 'result', result });
        })
        .catch((err: unknown) => {
          if (!cancelled) setOutcome({ kind: 'failed', message: apiErrorMessage(err, t('admin.regexTest.failed')) });
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [pattern, sample, t]);

  const result = outcome.kind === 'result' ? outcome.result : null;
  const matches = result && !result.error ? [...result.matches].sort((a, b) => a.start - b.start) : [];

  let status: string;
  let style: CSSProperties;
  if (!pattern.trim()) {
    status = t('admin.regexTest.empty');
    style = statusStyle('#eef3f7', '#5b6b7c');
  } else if (outcome.kind === 'failed' || result?.error) {
    status = t('admin.regexTest.invalid');
    style = statusStyle('#fbe8eb', '#c0102c');
  } else if (matches.length) {
    status = t('admin.regexTest.matches', { count: matches.length });
    style = statusStyle('#e2f5ef', '#0a5c48');
  } else {
    status = result ? t('admin.regexTest.none') : '';
    style = statusStyle('#fbf1de', '#7a4d00');
  }

  const segments: { text: string; match: boolean }[] = [];
  let last = 0;
  for (const match of matches) {
    if (match.start < last) continue;
    if (match.start > last) segments.push({ text: sample.slice(last, match.start), match: false });
    segments.push({ text: sample.slice(match.start, match.end), match: true });
    last = match.end;
  }
  if (last < sample.length) segments.push({ text: sample.slice(last), match: false });

  const errorText = outcome.kind === 'failed' ? outcome.message : (result?.error ?? null);

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-3.5">
      <label className="flex min-w-0 flex-col gap-1.5">
        <span className={SECTION}>{t('admin.regexTest.sample')}</span>
        <textarea
          value={sample}
          onChange={(event) => onSampleChange(event.target.value)}
          rows={9}
          className="w-full resize-y rounded border border-control bg-white px-3 py-2.5 font-[inherit] text-xs leading-[1.6] text-ink focus:border-primary focus:outline-none"
        />
      </label>
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className={SECTION}>{t('admin.regexTest.result')}</span>
          {status ? (
            <span className="px-[7px] py-0.5 text-[11px] font-semibold" style={style} title={errorText ?? undefined}>
              {status}
            </span>
          ) : null}
        </div>
        <div
          className="min-h-[150px] flex-1 whitespace-pre-wrap break-words border border-line bg-wash px-3 py-2.5 text-xs leading-[1.6] text-ink"
          aria-live="polite"
        >
          {segments.map((segment, index) =>
            segment.match ? (
              // biome-ignore lint/suspicious/noArrayIndexKey: segments have no identity beyond their order
              <mark key={index} style={MATCH_STYLE}>
                {segment.text}
              </mark>
            ) : (
              // biome-ignore lint/suspicious/noArrayIndexKey: segments have no identity beyond their order
              <span key={index}>{segment.text}</span>
            )
          )}
        </div>
        {errorText ? <span className="text-[11px] text-danger">{errorText}</span> : null}
      </div>
    </div>
  );
};
